import React, { useEffect, useMemo, useState } from "react";
import Loader from "@material-ui/core/CircularProgress";
import ThumbUpAltIcon from "@material-ui/icons/ThumbUpAlt";
import ThumbDownAltIcon from "@material-ui/icons/ThumbDownAlt";
import PlayCircleFilledIcon from "@material-ui/icons/PlayCircleFilled";
import { toast } from "react-toastify";

import DropdownFilter from "./DropdownFilter";
import ReactionVideoModal from "./ReactionVideoModal";
import {
  getAchievementVideoReactions,
  getAchievementVideoReactionDetail,
} from "../../api/FeedBacks";

// Reaction split-bar colours (match the approved mockup).
const LIKE_COLOR = "#639922";
const DISLIKE_COLOR = "#E24B4A";

const getUserToken = () => {
  try {
    const u = JSON.parse(localStorage.getItem("user"));
    return u && u.Token ? u.Token : null;
  } catch (e) {
    return null;
  }
};

// ExamType is delivered as 1 = TYT, 2 = AYT, 0 (or unknown) = "-".
const EXAM_TYPE_LABELS = { 1: "TYT", 2: "AYT" };
const examTypeLabel = (t) => EXAM_TYPE_LABELS[t] || "-";

// ─── Columns ───────────────────────────────────────────────────────────────────
// type drives custom cell rendering; filterable columns get a DropdownFilter.
const COLUMNS = [
  // { key: "AchievementVideoId", label: "Kazanım Videosu", sortable: true, type: "name" },
  // { key: "SegmentTopicId", label: "Segment", sortable: true, filterable: true, numeric: true },
  { key: "AchievementName", label: "Kazanım", sortable: true, type: "name" },
  { key: "CourseName", label: "Ders", sortable: true, filterable: true },
  { key: "ClassId", label: "ClassId", sortable: true, filterable: true },
  { key: "ExamTypeLabel", label: "Sınav Türü", sortable: true, filterable: true },
  { key: "SegmentTopicName", label: "Konu", sortable: true, filterable: true },
  { key: "Likes", label: "Beğeni", sortable: true, numeric: true, type: "like" },
  { key: "Dislike", label: "Beğenmeme", sortable: true, numeric: true, type: "dislike" },
  { key: "TotalReactions", label: "Toplam", sortable: true, numeric: true, type: "total" },
  { key: "LikeRatio", label: "Tepki dağılımı", sortable: true, type: "bar" },
  { key: "MaxDate", label: "Son Tepki", sortable: true, type: "date" },
];

const CSV_COLUMNS = [
  { key: "AchievementVideoId", label: "VideoId" },
  { key: "AchievementName", label: "Kazanım" },
  { key: "AchievementId", label: "KazanimId" },
  { key: "CourseName", label: "Ders" },
  { key: "ClassId", label: "ClassId" },
  { key: "ExamTypeLabel", label: "Sınav Türü" },
  { key: "SegmentTopicName", label: "Konu" },
  { key: "SegmentTopicId", label: "SegmentId" },
  { key: "Likes", label: "Beğeni" },
  { key: "Dislike", label: "Beğenmeme" },
  { key: "TotalReactions", label: "Toplam" },
  { key: "LikeRatio", label: "Beğeni Oranı %" },
  { key: "MaxDate", label: "Son Tepki" },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────
const applyFilters = (data, filters) =>
  data.filter((row) =>
    Object.entries(filters).every(([key, selected]) =>
      selected === null ? true : selected.includes(row[key])
    )
  );

const applySort = (data, sort) => {
  if (!sort.key || !sort.dir) return data;
  return [...data].sort((a, b) => {
    const va = a[sort.key];
    const vb = b[sort.key];
    if (typeof va === "number" && typeof vb === "number") {
      return sort.dir === "asc" ? va - vb : vb - va;
    }
    const sa = String(va ?? "").localeCompare(String(vb ?? ""), "tr", { sensitivity: "base" });
    return sort.dir === "asc" ? sa : -sa;
  });
};

const formatDate = (s) => {
  if (!s) return "-";
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  return d.toLocaleString("tr-TR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const exportToCSV = (data, filename) => {
  if (!data || data.length === 0) {
    toast.warning("Dışa aktarılacak veri yok.");
    return;
  }
  const header = CSV_COLUMNS.map((c) => c.label).join(",");
  const rows = data.map((row) =>
    CSV_COLUMNS.map((col) => {
      let val = col.key === "MaxDate" ? formatDate(row[col.key]) : row[col.key] ?? "";
      if (typeof val === "string" && (val.includes(",") || val.includes('"'))) {
        val = `"${val.replace(/"/g, '""')}"`;
      }
      return val;
    }).join(",")
  );
  const bom = "﻿"; // UTF-8 BOM so Excel renders Turkish chars
  const blob = new Blob([bom + [header, ...rows].join("\n")], {
    type: "text/csv;charset=utf-8;",
  });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// ─── Diverging reaction bar ──────────────────────────────────────────────────
const ReactionBar = ({ likes, dislikes }) => {
  const total = likes + dislikes;
  if (total === 0) return <span style={{ color: "#d1d5db" }}>—</span>;
  const likePct = Math.round((likes / total) * 100);
  const dislikePct = 100 - likePct;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 110 }}>
      <div
        style={{
          flex: 1,
          height: 14,
          borderRadius: 7,
          overflow: "hidden",
          display: "flex",
          backgroundColor: "#e5e7eb",
          minWidth: 70,
        }}
        title={`${likes} beğeni · ${dislikes} beğenmeme`}
      >
        <div style={{ width: `${likePct}%`, backgroundColor: LIKE_COLOR }} />
        <div style={{ width: `${dislikePct}%`, backgroundColor: DISLIKE_COLOR }} />
      </div>
      <span style={{ fontSize: 12, color: "#374151", minWidth: 34, textAlign: "right" }}>
        {likePct}%
      </span>
    </div>
  );
};

// ─── Main table ──────────────────────────────────────────────────────────────
const AchievementReactionsTable = () => {
  const [data, setData] = useState([]);
  const [loader, setLoader] = useState(false);
  const [filters, setFilters] = useState({ CourseName: null, SegmentTopicName: null,ClassId: null,ExamTypeLabel: null });
  const [sort, setSort] = useState({ key: "TotalReactions", dir: "desc" });

  // Modal: selectedRow holds the list row (aggregate header), detail is fetched.
  const [selectedRow, setSelectedRow] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadData = async () => {
    try {
      setLoader(true);
      const res = await getAchievementVideoReactions(
        { StatusType: 0, MonthCount: 0, OrderBy: 1 },
        getUserToken()
      );
      if (res.status === 200) {
        // setData(res.data?.ResponseData ?? []);
        const rows = (res.data?.ResponseData ?? []).map((r) => ({
          ...r,
          ExamTypeLabel: examTypeLabel(r.ExamType),
        }));
        setData(rows);
      } else {
        toast.error("Tepki verileri alınamadı.");
      }
    } catch (e) {
      console.error("AchievementReactions loadData error:", e);
      toast.error("Veri yüklenirken hata oluştu.");
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFilterChange = (key, val) =>
    setFilters((prev) => ({ ...prev, [key]: val }));

  const handleHeaderClick = (col) => {
    if (!col.sortable) return;
    setSort((prev) => {
      if (prev.key !== col.key) return { key: col.key, dir: "asc" };
      if (prev.dir === "asc") return { key: col.key, dir: "desc" };
      return { key: null, dir: null };
    });
  };

  const getSortIcon = (colKey) => {
    if (sort.key !== colKey) return "↕";
    return sort.dir === "asc" ? "↑" : "↓";
  };

  const filterableColumns = COLUMNS.filter((c) => c.filterable);

  const filteredData = useMemo(() => applyFilters(data, filters), [data, filters]);
  const displayData = useMemo(() => applySort(filteredData, sort), [filteredData, sort]);
  const anyFilterActive = Object.values(filters).some((v) => v !== null);

  // Cascading options: each filter's choices come from data filtered by the OTHERS.
  const optionsDataByKey = useMemo(() => {
    const result = {};
    filterableColumns.forEach((col) => {
      const otherFilters = Object.fromEntries(
        Object.entries(filters).filter(([k]) => k !== col.key)
      );
      result[col.key] = applyFilters(data, otherFilters);
    });
    return result;
  }, [data, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const clearAllFilters = () =>
    filterableColumns.forEach((col) => handleFilterChange(col.key, null));

  const openDetail = async (row) => {
    setSelectedRow(row);
    setDetail(null);
    setDetailLoading(true);
    try {
      const res = await getAchievementVideoReactionDetail(
        row.AchievementVideoId,
        getUserToken()
      );
      if (res.status === 200 && res.data?.ResponseData) {
        setDetail(res.data.ResponseData);
      } else {
        toast.error("Video detayı alınamadı.");
      }
    } catch (e) {
      console.error("AchievementReactions detail error:", e);
      toast.error("Detay yüklenirken hata oluştu.");
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setSelectedRow(null);
    setDetail(null);
  };

  const renderCell = (col, row) => {
    switch (col.type) {
      case "name":
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <PlayCircleFilledIcon style={{ fontSize: 22, color: "#2563eb", flexShrink: 0 }} />
            <div style={{ overflow: "hidden" }}>
              {/* <div style={{ color: "#111827", whiteSpace: "nowrap", fontWeight: 500 }}>
                Video #{row.AchievementVideoId} */}
                  <div
                title={row.AchievementName}
                style={{
                  color: "#111827",
                  fontWeight: 500,
                  maxWidth: 320,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {row.AchievementName || `Video #${row.AchievementVideoId}`}
              </div>
              <div style={{ fontSize: 11, color: "#9ca3af" }}>
                {/* Kazanım {row.AchievementId} */}
                Video #{row.AchievementVideoId} · Kazanım {row.AchievementId}
              </div>
            </div>
          </div>
        );
      case "like":
        return (
          <span style={{ color: row.Likes > 0 ? "#15803d" : "#d1d5db", fontWeight: 500 }}>
            {row.Likes > 0 ? row.Likes.toLocaleString("tr-TR") : "—"}
          </span>
        );
      case "dislike":
        return (
          <span style={{ color: row.Dislike > 0 ? "#b91c1c" : "#d1d5db", fontWeight: 500 }}>
            {row.Dislike > 0 ? row.Dislike.toLocaleString("tr-TR") : "—"}
          </span>
        );
      case "total":
        return (
          <span style={{ color: "#111827", fontWeight: 700 }}>
            {(row.TotalReactions ?? 0).toLocaleString("tr-TR")}
          </span>
        );
      case "bar":
        return <ReactionBar likes={row.Likes} dislikes={row.Dislike} />;
      case "date":
        return <span style={{ color: "#6b7280" }}>{formatDate(row.MaxDate)}</span>;
      default:
        return row[col.key] ?? "-";
    }
  };

  // Footer totals (respect active filters, like the CMSAnalysis footer).
  const totalLikes = displayData.reduce((s, r) => s + (r.Likes || 0), 0);
  const totalDislikes = displayData.reduce((s, r) => s + (r.Dislike || 0), 0);
  const totalReactions = displayData.reduce((s, r) => s + (r.TotalReactions || 0), 0);

  return (
    <div>
      {loader && (
        <div
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 9999,
          }}
        >
          <Loader size={70} />
        </div>
      )}

      {/* Filter bar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 14,
          marginTop: 14,
          flexWrap: "wrap",
        }}
      >
        {filterableColumns.map((col) => (
          <DropdownFilter
            key={col.key}
            columnKey={col.key}
            label={col.label}
            optionsData={optionsDataByKey[col.key]}
            selectedValues={filters[col.key]}
            onChange={(val) => handleFilterChange(col.key, val)}
          />
        ))}

        {anyFilterActive && (
          <button
            onClick={clearAllFilters}
            style={{
              padding: "7px 12px",
              fontSize: 13,
              border: "1px solid #fca5a5",
              borderRadius: 6,
              backgroundColor: "#fef2f2",
              color: "#dc2626",
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            ✕ Tüm Filtreleri Temizle
          </button>
        )}

        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 13, color: "#6b7280", whiteSpace: "nowrap" }}>
            {filteredData.length} / {data.length} satır
            {sort.key && (
              <span style={{ color: "#2563eb", marginLeft: 6 }}>
                · {sort.key} {sort.dir === "asc" ? "↑" : "↓"}
              </span>
            )}
          </span>
          <button
            onClick={loadData}
            disabled={loader}
            style={{
              padding: "7px 12px",
              fontSize: 13,
              border: "1px solid #d1d5db",
              borderRadius: 6,
              backgroundColor: "#fff",
              color: "#374151",
              cursor: loader ? "not-allowed" : "pointer",
              fontWeight: 600,
              whiteSpace: "nowrap",
            }}
          >
            ↺ Yenile
          </button>
          <button
            onClick={() => exportToCSV(displayData, "Kazanim_Video_Tepkileri.csv")}
            style={{
              padding: "7px 14px",
              backgroundColor: "#16a34a",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              cursor: "pointer",
              fontWeight: 600,
              fontSize: 13,
              whiteSpace: "nowrap",
            }}
          >
            ⬇ Excel'e Aktar
          </button>
        </div>
      </div>

      {/* Table */}
      <div
        style={{
          overflowX: "auto",
          borderRadius: 8,
          boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
        }}
      >
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ backgroundColor: "#1e3a5f" }}>
              {COLUMNS.map((col) => (
                <th
                  key={col.key}
                  onClick={() => handleHeaderClick(col)}
                  style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                    backgroundColor: "#1e3a5f",
                    padding: "11px 14px",
                    textAlign: col.numeric ? "right" : "left",
                    color: "#fff",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    borderRight: "1px solid #2d5282",
                    cursor: col.sortable ? "pointer" : "default",
                    userSelect: "none",
                    minWidth: col.type === "name" ? 240 : col.type === "bar" ? 150 : 90,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: col.numeric ? "flex-end" : "space-between",
                      gap: 6,
                    }}
                  >
                    <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      {col.type === "like" && <ThumbUpAltIcon style={{ fontSize: 15 }} />}
                      {col.type === "dislike" && <ThumbDownAltIcon style={{ fontSize: 15 }} />}
                      {col.label}
                    </span>
                    {col.sortable && (
                      <span
                        style={{
                          fontSize: 11,
                          opacity: sort.key === col.key ? 1 : 0.35,
                          flexShrink: 0,
                        }}
                      >
                        {getSortIcon(col.key)}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {displayData.length === 0 ? (
              <tr>
                <td
                  colSpan={COLUMNS.length}
                  style={{ textAlign: "center", padding: 40, color: "#6b7280", fontSize: 14 }}
                >
                  {data.length === 0
                    ? loader
                      ? "Yükleniyor..."
                      : "Veri bulunamadı."
                    : "Seçilen filtrelere uyan satır yok."}
                </td>
              </tr>
            ) : (
              displayData.map((row) => (
                <tr
                  key={row.AchievementVideoId}
                  onClick={() => openDetail(row)}
                  style={{
                    backgroundColor: "#ffffff",
                    borderBottom: "1px solid #e2e8f0",
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#eff6ff")}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#ffffff")}
                >
                  {COLUMNS.map((col) => (
                    <td
                      key={col.key}
                      style={{
                        padding: "8px 14px",
                        borderRight: "1px solid #e2e8f0",
                        textAlign: col.numeric ? "right" : "left",
                        whiteSpace: col.type === "bar" ? "normal" : "nowrap",
                      }}
                    >
                      {renderCell(col, row)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>

          {displayData.length > 0 && (
            <tfoot>
              <tr style={{ backgroundColor: "#e8f0fe", borderTop: "2px solid #93c5fd" }}>
                <td style={{ padding: "9px 14px", fontWeight: 700, color: "#1e3a5f", fontSize: 12 }}>
                  Toplam ({displayData.length} satır)
                </td>
                <td style={{ borderRight: "1px solid #bfdbfe" }} />
                <td style={{ borderRight: "1px solid #bfdbfe" }} />
                <td style={{ borderRight: "1px solid #bfdbfe" }} />
                <td style={{ borderRight: "1px solid #bfdbfe" }} />
                <td style={{ padding: "9px 14px", fontWeight: 700, color: "#15803d", textAlign: "right", borderRight: "1px solid #bfdbfe" }}>
                  {totalLikes.toLocaleString("tr-TR")}
                </td>
                <td style={{ padding: "9px 14px", fontWeight: 700, color: "#b91c1c", textAlign: "right", borderRight: "1px solid #bfdbfe" }}>
                  {totalDislikes.toLocaleString("tr-TR")}
                </td>
                <td style={{ padding: "9px 14px", fontWeight: 700, color: "#1e3a5f", textAlign: "right", borderRight: "1px solid #bfdbfe" }}>
                  {totalReactions.toLocaleString("tr-TR")}
                </td>
                <td style={{ padding: "9px 14px", borderRight: "1px solid #bfdbfe" }}>
                  <ReactionBar likes={totalLikes} dislikes={totalDislikes} />
                </td>
                <td style={{ borderRight: "1px solid #bfdbfe" }} />
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      <ReactionVideoModal
        isOpen={selectedRow !== null}
        onClose={closeDetail}
        row={selectedRow}
        detail={detail}
        loading={detailLoading}
      />
    </div>
  );
};

export default AchievementReactionsTable;
